import { useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Modal from '@/components/common/modal';
import { useCreateCommercialFeatureMutation } from '@/state/mutations/admin-commercial-features/use-create-commercial-feature-mutation';
import { useUpdateCommercialFeatureMutation } from '@/state/mutations/admin-commercial-features/use-update-commercial-feature-mutation';
import type { CommercialFeature } from '@/types/dtos/commercial-features/commercial-feature.dto';

const SCOPE_OPTIONS = ['Organization', 'Event', 'Flow', 'Venue'] as const;

const keySchema = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[a-z0-9][a-z0-9_-]*$/, 'Lowercase slug: letter/number, then a-z0-9_-');

function buildFormSchema(isEdit: boolean) {
  return z.object({
    key: isEdit ? z.string().optional() : keySchema,
    displayName: z.string().min(1).max(256),
    description: z.string().max(4000).optional(),
    allowedScopeTypes: z.array(z.enum(['Organization', 'Event', 'Flow', 'Venue'])).min(1),
    category: z.string().max(128).optional(),
    deprecated: z.boolean().optional(),
    replacedByKey: z.string().max(128).optional(),
  });
}

export type CommercialFeatureFormValues = z.infer<ReturnType<typeof buildFormSchema>>;

export default function CommercialFeatureAdminFormModal({
  open,
  onClose,
  feature,
}: {
  open: boolean;
  onClose: () => void;
  feature: CommercialFeature | null;
}) {
  const createMut = useCreateCommercialFeatureMutation();
  const updateMut = useUpdateCommercialFeatureMutation();
  const isEdit = !!feature?.id;

  const formSchema = useMemo(() => buildFormSchema(isEdit), [isEdit]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CommercialFeatureFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      key: '',
      displayName: '',
      description: '',
      allowedScopeTypes: ['Organization'],
      category: '',
      deprecated: false,
      replacedByKey: '',
    },
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    if (feature) {
      reset({
        key: feature.key,
        displayName: feature.displayName,
        description: feature.description ?? '',
        allowedScopeTypes: [...feature.allowedScopeTypes],
        category: feature.category ?? '',
        deprecated: feature.deprecated,
        replacedByKey: feature.replacedByKey ?? '',
      });
    } else {
      reset({
        key: '',
        displayName: '',
        description: '',
        allowedScopeTypes: ['Organization'],
        category: '',
        deprecated: false,
        replacedByKey: '',
      });
    }
  }, [open, feature, reset]);

  const onSubmit = async (values: CommercialFeatureFormValues) => {
    try {
      if (isEdit && feature?.id) {
        await updateMut.mutateAsync({
          id: feature.id,
          body: {
            displayName: values.displayName.trim(),
            description: values.description?.trim() || undefined,
            allowedScopeTypes: values.allowedScopeTypes,
            category: values.category?.trim() || undefined,
            deprecated: values.deprecated,
            replacedByKey: values.replacedByKey?.trim() ? values.replacedByKey.trim() : null,
          },
        });
      } else {
        await createMut.mutateAsync({
          key: (values.key ?? '').trim(),
          displayName: values.displayName.trim(),
          description: values.description?.trim() || undefined,
          allowedScopeTypes: values.allowedScopeTypes,
          category: values.category?.trim() || undefined,
          deprecated: values.deprecated ?? false,
          replacedByKey: values.replacedByKey?.trim() || undefined,
        });
      }
      onClose();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const pending = createMut.isPending || updateMut.isPending;

  return (
    <Modal
      open={open}
      handleClose={onClose}
      title={isEdit ? 'Edit commercial feature' : 'Create commercial feature'}
      description="Registry keys used in subscription plan featureKeys and FEATURE/PACKAGE products. Quota keys stay separate."
      size="md"
      contentSx={{ maxHeight: 'min(85vh, 640px)', overflowY: 'auto' }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {isEdit ? (
          <TextField label="Key (read-only)" value={feature?.key ?? ''} size="small" fullWidth disabled />
        ) : (
          <Controller
            name="key"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Key"
                size="small"
                fullWidth
                required
                error={!!errors.key}
                helperText={errors.key?.message ?? 'e.g. monetize — lowercase slug'}
              />
            )}
          />
        )}
        <Controller
          name="displayName"
          control={control}
          render={({ field }) => (
            <TextField {...field} label="Display name" size="small" fullWidth required error={!!errors.displayName} />
          )}
        />
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField {...field} label="Description" size="small" fullWidth multiline minRows={2} />
          )}
        />
        <Controller
          name="allowedScopeTypes"
          control={control}
          render={({ field }) => (
            <FormControl component="fieldset" variant="standard" error={!!errors.allowedScopeTypes}>
              <FormLabel component="legend">Allowed scopes</FormLabel>
              <FormGroup>
                {SCOPE_OPTIONS.map((scope) => (
                  <FormControlLabel
                    key={scope}
                    control={
                      <Checkbox
                        checked={field.value?.includes(scope) ?? false}
                        onChange={(_, checked) => {
                          const next = new Set(field.value ?? []);
                          if (checked) {
                            next.add(scope);
                          } else {
                            next.delete(scope);
                          }
                          field.onChange([...next]);
                        }}
                      />
                    }
                    label={scope}
                  />
                ))}
              </FormGroup>
              {errors.allowedScopeTypes ? (
                <Typography variant="caption" color="error">
                  {String(errors.allowedScopeTypes.message)}
                </Typography>
              ) : null}
            </FormControl>
          )}
        />
        <Controller
          name="category"
          control={control}
          render={({ field }) => <TextField {...field} label="Category (optional)" size="small" fullWidth />}
        />
        <Controller
          name="deprecated"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
              label="Deprecated"
            />
          )}
        />
        <Controller
          name="replacedByKey"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Replaced by key (optional)"
              size="small"
              fullWidth
              helperText="Successor key when deprecated"
            />
          )}
        />
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={pending}>
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}
